import { ConceptAudioTrack } from '../types';

export const DEFAULT_NURSING_CONCEPT_TRACKS: ConceptAudioTrack[] = [
  {
    id: 'concept-vitals-1',
    title: 'Vital Signs: Normal Ranges & Measurement Rules',
    conceptCategory: 'Clinical & Vitals',
    duration: '3:45',
    img: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=300&auto=format&fit=crop&q=80',
    source: 'https://cdn.pixabay.com/download/audio/2022/05/27/audio_1808fbf07a.mp3?filename=ambient-piano-10781.mp3',
    script: 'Vital signs monitor baseline physiological status. Normal adult oral temperature ranges from 97.6°F to 99.6°F. Radial pulse is palpated for 30 seconds and multiplied by 2 if regular, but apical pulse must be auscultated at the 5th intercostal space, left midclavicular line for 1 full minute. Normal resting blood pressure is under 120/80 mmHg. Report a systolic above 140 or diastolic above 90 to the charge nurse immediately.',
    highYieldPoints: [
      'Apical pulse: Always count for 1 full minute at 5th ICS, left MCL',
      'Normal blood pressure: < 120/80 mmHg',
      'Respirations: Count discreetly after pulse without telling resident'
    ]
  },
  {
    id: 'concept-infection-2',
    title: 'Infection Control: Donning & Doffing PPE Protocol',
    conceptCategory: 'Safety & Prevention',
    duration: '4:12',
    img: 'https://images.unsplash.com/photo-1584515933487-779824d29309?w=300&auto=format&fit=crop&q=80',
    source: 'https://cdn.pixabay.com/download/audio/2022/01/18/audio_d0a13f69d2.mp3?filename=soft-ambient-111154.mp3',
    script: 'Chain of infection requires infectious agent, reservoir, portal of exit, mode of transmission, portal of entry, and susceptible host. Donning PPE order before entering room: Gown, Mask or N95, Goggles/Face Shield, Gloves. Doffing PPE order before exiting: Gloves, Goggles, Gown, Mask. Hand hygiene must be performed for at least 20 seconds before and after all patient contact.',
    highYieldPoints: [
      'Donning sequence: Gown → Mask → Goggles → Gloves',
      'Doffing sequence: Gloves → Goggles → Gown → Mask',
      'Hand hygiene: Friction for at least 20 seconds with warm water'
    ]
  },
  {
    id: 'concept-transfer-3',
    title: 'Patient Transfers, Gait Belts & Body Mechanics',
    conceptCategory: 'ADLs & Mobility',
    duration: '3:20',
    img: 'https://images.unsplash.com/photo-1581594693702-fbdc51b2763b?w=300&auto=format&fit=crop&q=80',
    source: 'https://cdn.pixabay.com/download/audio/2022/03/15/audio_c8c8a731ef.mp3?filename=chill-abstract-12099.mp3',
    script: 'Proper body mechanics require maintaining a wide base of support with feet shoulder-width apart, bending at the knees rather than the waist, and holding heavy objects close to your center of gravity. Always apply a gait belt snugly over clothing when assisting unsteady residents with transfers or ambulation. Lock all wheelchair and bed wheels prior to transfer.',
    highYieldPoints: [
      'Wide base of support: Feet shoulder-width apart, knees flexed',
      'Gait belt placement: Over resident clothing at waist level',
      'Transfer rule: Always pivot toward resident\'s stronger side'
    ]
  },
  {
    id: 'concept-adls-4',
    title: 'ADLs, Bed Bathing & Pressure Ulcer Prevention',
    conceptCategory: 'Personal ADL Care',
    duration: '4:05',
    img: 'https://images.unsplash.com/photo-1516549655169-df83a0774514?w=300&auto=format&fit=crop&q=80',
    source: 'https://cdn.pixabay.com/download/audio/2022/10/14/audio_9939f78322.mp3?filename=relaxing-light-piano-124376.mp3',
    script: 'Activities of daily living encompass bathing, dressing, grooming, eating, and toileting. When bathing, clean from cleanest to dirtiest area. Perform perineal care wiping front to back to prevent urinary tract infections. Reposition bedbound residents at least every 2 hours to prevent pressure injuries over bony prominences such as the sacrum, heels, and elbows.',
    highYieldPoints: [
      'Perineal hygiene: Always wipe front-to-back (clean to dirty)',
      'Pressure relief: Turn & reposition at least every 2 hours',
      'Stage 1 Ulcer: Non-blanchable redness over bony prominence'
    ]
  },
  {
    id: 'concept-rights-5',
    title: 'Resident Rights, HIPAA & Mandatory Reporting',
    conceptCategory: 'Principles & Rights',
    duration: '3:15',
    img: 'https://images.unsplash.com/photo-1450133064473-71024230f91b?w=300&auto=format&fit=crop&q=80',
    source: 'https://cdn.pixabay.com/download/audio/2022/05/27/audio_1808fbf07a.mp3?filename=ambient-piano-10781.mp3',
    script: 'OBRA legislation guarantees long-term care residents privacy, dignity, freedom from restraints, and the right to refuse care. HIPAA protects all electronic, spoken, and written health information. CNAs are mandatory reporters under the law; any observed or suspected signs of physical, emotional, or financial abuse must be reported immediately to the nurse manager.',
    highYieldPoints: [
      'OBRA standard: 75 hours minimum training, resident autonomy',
      'Mandatory reporting: Immediate notification to supervisor upon suspicion',
      'Restraints: Requires physician order; release every 2 hours'
    ]
  },
  {
    id: 'concept-dementia-6',
    title: 'Dementia Care, Sundowning & Validation Therapy',
    conceptCategory: 'Principles & Rights',
    duration: '4:30',
    img: 'https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?w=300&auto=format&fit=crop&q=80',
    source: 'https://cdn.pixabay.com/download/audio/2022/01/18/audio_d0a13f69d2.mp3?filename=soft-ambient-111154.mp3',
    script: 'Dementia causes irreversible decline in memory and executive functioning. For residents experiencing sundowning or late-afternoon agitation, dim harsh overhead lights and maintain quiet routine. Use validation therapy to acknowledge emotional feelings rather than challenging delusions when re-orientation triggers anxiety.',
    highYieldPoints: [
      'Sundowning: Increased confusion in late afternoon/evening',
      'Validation Therapy: Validate resident feelings without arguing',
      'De-escalation: Speak in slow, soft, reassuring tone at eye level'
    ]
  },
  {
    id: 'concept-nutrition-7',
    title: 'Nutrition, Dysphagia & Fluid Intake Calculations',
    conceptCategory: 'Clinical & Vitals',
    duration: '3:50',
    img: 'https://images.unsplash.com/photo-1498837167922-ddd27525d352?w=300&auto=format&fit=crop&q=80',
    source: 'https://cdn.pixabay.com/download/audio/2022/03/15/audio_c8c8a731ef.mp3?filename=chill-abstract-12099.mp3',
    script: 'Accurate intake and output tracking is essential for fluid balance assessment. 1 fluid ounce equals 30 milliliters or cc. For residents with dysphagia, serve ordered thickened liquids (nectar, honey, or pudding thick) and maintain upright 90-degree seating position during meals and 30 minutes post-meal to prevent aspiration pneumonia.',
    highYieldPoints: [
      'Conversion rule: 1 oz = 30 mL (or 30 cc)',
      'Dysphagia care: Sit upright 90 degrees during & 30 mins after meal',
      'Aspiration signs: Coughing, wet gurgly voice while eating'
    ]
  },
  {
    id: 'concept-catheter-8',
    title: 'Urinary Catheter Care & Specimen Collection',
    conceptCategory: 'Clinical & Vitals',
    duration: '3:10',
    img: 'https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?w=300&auto=format&fit=crop&q=80',
    source: 'https://cdn.pixabay.com/download/audio/2022/10/14/audio_9939f78322.mp3?filename=relaxing-light-piano-124376.mp3',
    script: 'Indwelling Foley catheter drainage bags must always hang below the level of the resident bladder to prevent urine backflow and infection. Clean the catheter tubing starting at the meatus and wiping downward at least 4 inches away with soap and warm water during routine hygiene.',
    highYieldPoints: [
      'Drainage bag placement: Below bladder level on bed frame (not floor)',
      'Catheter hygiene: Clean 4 inches downward away from meatus',
      'Clean catch specimen: Clean meatus first, catch midstream urine'
    ]
  },
  {
    id: 'concept-emergency-9',
    title: 'Emergency Protocols: RACE, PASS & Seizure Safety',
    conceptCategory: 'Safety & Prevention',
    duration: '3:30',
    img: 'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=300&auto=format&fit=crop&q=80',
    source: 'https://cdn.pixabay.com/download/audio/2022/05/27/audio_1808fbf07a.mp3?filename=ambient-piano-10781.mp3',
    script: 'In case of fire, execute RACE: Rescue residents in immediate danger, Activate alarm, Contain fire by closing doors, Extinguish if safe. Operate extinguisher with PASS: Pull pin, Aim nozzle at base of fire, Squeeze lever, Sweep side to side. During a seizure, lower resident to floor, turn to side to clear airway, and protect head with a soft pillow.',
    highYieldPoints: [
      'RACE protocol: Rescue → Alarm → Contain → Extinguish',
      'PASS protocol: Pull → Aim → Squeeze → Sweep',
      'Seizure response: Turn to side, protect head, DO NOT insert anything in mouth'
    ]
  },
  {
    id: 'concept-math-10',
    title: 'Pharmacology Basics & Metric Conversions',
    conceptCategory: 'Board Prep & Basics',
    duration: '3:00',
    img: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=300&auto=format&fit=crop&q=80',
    source: 'https://cdn.pixabay.com/download/audio/2022/01/18/audio_d0a13f69d2.mp3?filename=soft-ambient-111154.mp3',
    script: 'Essential nursing metric conversions: 1 kilogram equals 2.2 pounds. 1 teaspoon equals 5 mL. 1 tablespoon equals 15 mL. Always verify the Six Rights of Medication Administration: Right Patient, Right Drug, Right Dose, Right Route, Right Time, Right Documentation.',
    highYieldPoints: [
      'Weight conversion: lbs ÷ 2.2 = kg',
      'Liquid conversion: 1 tsp = 5 mL | 1 tbsp = 15 mL | 1 oz = 30 mL',
      '6 Rights: Patient, Drug, Dose, Route, Time, Documentation'
    ]
  }
];
